/**
 * @module app.ext.mcg-informed
 */

import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { Router } from 'aurelia-router';
import { ComponentBase, } from 'app/shared/lib/essette-site';
import { AssessmentService } from 'app/core/services/assessment-service';
import { CaseService } from 'app/cm/services/case-service';
import { McgInformedService } from 'app/ext/mcg-informed/services/mcg-informed-service';
import { AssessmentContext } from 'app/ext/mcg-informed/assessment-context';
import { QuestionResolver as CoreQuestionResolver } from 'app/core/components/assessments/question-resolver';
import { QuestionResolver } from 'app/ext/mcg-informed/question-resolver';
import { IAssessmentsConfig, ITasksConfig } from 'app/core/models/essette-interfaces';
import { apiCall } from 'app/shared/lib/essette-api';
import { getReference } from 'app/shared/models/essette-types';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { IApiSettings, IApiActionResults, IQueryLoadOptions, IApiQueryResults } from 'app/shared/lib/api/api-types';
import { Toaster } from 'app/shared/lib/toaster';
import { safe } from 'app/shared/lib/utilities';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { fireEvents } from 'app/shared/lib/fire-events';
import { ExtendLocalState } from 'app/shared/models/local-state-extensions';
import { IBaseLocalState } from 'app/shared/models/local-state-extensions';
import { EssDialogCustomElement } from 'app/shared/elements/ess-dialog';
import { AssessmentWithLocalState } from 'app/core/models/local-state-extensions';
import { guid } from 'app/shared/lib/guid';
import { EventAggregator } from 'aurelia-event-aggregator';
import { EnableReturnFocusEvent, TitleChangeEvent } from 'app/shared/lib/events';
import { I18N } from 'aurelia-i18n';
import { replaceCopyrightText } from 'app/ext/mcg-informed/lib/utilities';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import * as Core from 'app/core/models/essette-api';
import * as Mcg from 'app/ext/mcg-informed/interfaces';
import * as CM from 'app/cm/models/essette-api';
import * as CD from 'app/cm/models/essette-api';

export interface IAssessmentAttachmentOptions {
    showToc: boolean;
    showBack: boolean;
    showActions: boolean;
    showMember: boolean;
    showMemberSummaryLink: boolean,
    showTarget: boolean;
    showTargetSummaryLink: boolean,
    stickyActions: boolean;
}

const defaultOptions: IAssessmentAttachmentOptions = {
    showToc: true,
    showBack: true,
    showActions: true,
    showMember: true,
    showMemberSummaryLink: false,
    showTarget: true,
    showTargetSummaryLink: false,
    stickyActions: true
}

export type CaseAssessmentAttachmentWithLocalState = ExtendLocalState<CM.CaseAssessmentAttachment, IBaseLocalState>;

@autoinject()
export class AssessmentAttachmentCustomElement extends ComponentBase {

    ////****************** Fields and Properties **************************************////
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public config: IAssessmentsConfig | null = null;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public taskConfig: ITasksConfig | null = null;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public options: IAssessmentAttachmentOptions = defaultOptions;

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'loadData' })
    public attachmentId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = false;

    @bindable
    public summary: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public target: Readonly<{
        id: string;
        number: string;
        member: CD.Member;
    }> | null = null;


    protected assessment: Mcg.Assessment | null = null;
    protected attachment: CaseAssessmentAttachmentWithLocalState | null = null;
    protected session: Core.AssessmentSession | null = null;
    protected existingAttachments: string[] = [];

    protected coreSections: Core.AssessmentSection[] = [];
    protected sections: Mcg.SubSection[] | null = null;

    protected $carePlanModal!: EssDialogCustomElement;

    protected showMemberSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;

    protected showCaseSummaryLink: boolean = false;
    protected openCaseSummary = openCaseSummary;

    protected mcgCopyright?: string;
    protected mcgCustomContentDisclaimer?: string;

    protected id: guid = guid();

    ////****************** Lifecycle events (in order of execution) ******************////

    constructor(
        private readonly $element: Element,
        private readonly context: AssessmentContext,
        private readonly service: AssessmentService,
        private readonly caseService: CaseService,
        private readonly mcgService: McgInformedService,
        protected readonly resolver: QuestionResolver,
        protected readonly coreResolver: CoreQuestionResolver,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        protected router: Router,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected async bind(): Promise<void> {
        if(!this.config) { throw new Error('Bindable property [config] was not set'); }
        if(!this.attachmentId) { throw new Error('Bindable property [attachment-id] was not set'); }

        this.service.configure(this.config);

        this.options = { ...defaultOptions, ...this.options };

        this.loadData(this.attachmentId);

        this.options.showToc = this.sections?.length! > 1;
    }

    protected detached(): void {
        this.context.clear();
        this.sections = null;
    }

    ////****************** Functions ***********************************************////

    public async reload(): Promise<void> {
        if(!this.attachmentId) return;
        this.loadData(this.attachmentId);
    }

    private async loadData(attachmentId: string): Promise<void> {
        this.context.clear();

        await this.loadAttachment(attachmentId);

        if(!this.attachment?.externalContent) {
            const mcgAttachments = await this.loadMcgAttachments([attachmentId]);
            const mcgAssessments = await this.loadMcgAssessments(mcgAttachments);
            const externalContentSuccess = await this.addMcgAttachmentExternalContent(mcgAttachments, mcgAssessments);
            if(externalContentSuccess) await this.loadAttachment(attachmentId);
        }

        if(!this.attachment) { throw new Error('No assessment attachment was loaded'); }

        await this.loadCaseAttachments();

        let assessment: CM.CaseAssessmentWithVersionModel | null | undefined = this.attachment.assessment;
        if(!assessment) { throw new Error('No assessment was loaded'); }

        let numberOfSections = assessment.currentVersion?.sections?.length;
        if(!numberOfSections || numberOfSections === 0) {
            await this.mcgService.createObjects(this.attachment);

            // Reload attachment after creating objects
            await this.loadAttachment(attachmentId);
            if(!this.attachment) { throw new Error('No assessment attachment was loaded'); }
        }

        // Set Page title to Assessment name
        if(!!this.config?.backRouteName && !!this.attachment.$display) {
            this.events.publish(new TitleChangeEvent([this.attachment.$display]));
        }
        this.router.title = this.i18n.tr('cm:routes.mcg-informed.assessment.$title', { what: this.attachment.$display });
        this.router.updateTitle();

        // if summary is true, then assessment will be readonly, otherwise check if assessment is completed
        if(!this.summary) this.readonly = this.attachment.isCompleted || false;
        if(!this.env.config.integrations.includes('cci')) this.readonly = true;

        if(this.attachment.lastSession && this.attachment.lastSession.id) await this.loadSession(this.attachment.lastSession.id);

        this.context.attachment = this.attachment;
        if(this.taskConfig) this.context.taskConfig = this.taskConfig;
        this.context.currentSession = {
            id: this.session?.id,
            attachment: getReference(this.attachment)
        };

        await this.loadMcgAssessmentStructure();
        await this.loadAssessmentStructure();
    }

    private async loadMcgAssessmentStructure(): Promise<void> {
        if(!this.attachment || !this.attachment.externalContent) return;

        const content = this.attachment.externalContent.content;
        if(!content) throw new Error('Assessment attachment content was not loaded');

        this.assessment = JSON.parse(content);
        if(!this.assessment || !this.assessment.sectionsSet || this.assessment.sectionsSet.length === 0) throw new Error('Error parsing assessment json');

        // Set Edition Number and Year in Copyright
        const settings = await this.loadSettings();
        const copyright = getApiSetting(settings, 'mcg.informed.copyright.$display')?.toString();
        const edition = this.attachment.assessment?.currentVersion?.versionName ? this.attachment.assessment.currentVersion.versionName.split('.')[0] : getApiSetting(settings, 'mcg.informed.edition.$display')?.toString();
        if(copyright) this.mcgCopyright = replaceCopyrightText(copyright, edition);

        // Set custom content disclaimer based on parsed content
        if(this.attachment.assessment?.externalSystem?.code?.toLowerCase() === 'cci' && this.attachment.externalContent?.content) {
            const content = this.attachment.externalContent.content;
            const assessment: Mcg.Assessment = JSON.parse(content);
            this.mcgCustomContentDisclaimer = assessment.customGuidelineDisclaimer;
        } else {
            this.mcgCustomContentDisclaimer = undefined;
        }

        this.sections = this.assessment.sectionsSet[0].sectionsSet;
    }

    protected createQuestionConfig(question: Mcg.Question, labelId: string = ''): Mcg.IQuestionConfig {
        return {
            question: question,
            questionNumberDisplay: question.displayValue.toLowerCase(),
            readonly: this.summary || this.attachment?.isCompleted || !this.env.config.integrations.includes('cci'),
            existingAttachments: this.existingAttachments,
            labelId: labelId
        } as Mcg.IQuestionConfig;
    }

    protected closeCarePlanModal(): void {
        this.$carePlanModal.close();
    }

    public async save(complete: boolean = false): Promise<void> {
        if(!this.attachment || !this.attachment.externalContent) return;
        if(!complete && !this.context.unsavedChanges) {
            this.toaster.warn().message(this.i18n.tr('ui:messages.warnings.noChangesDetected')).show();
            return;
        }

        let attachment: CM.CaseAssessmentAttachment | null = null;
        let assessment: Mcg.Assessment = this.assessment!;

        const selectedChoices = new Array<Mcg.Choice>();
        const selectedGuidelineLinks = new Array<Mcg.GuidelineLink>();

        const answers = new Array<Core.AssessmentAnswer>();

        this.context.questionComponents.forEach(q => {
            if(!!q.getAnswers) {
                const a = q.getAnswers() || [];
                selectedChoices.push(...a);
            }

            if(!!q.getGuidelineLinks) {
                const gl = q.getGuidelineLinks() || [];
                selectedGuidelineLinks.push(...gl);
            }
        });

        assessment?.sectionsSet?.forEach(section => {
            section.sectionsSet.forEach(subSection => {
                subSection.questionsSet.forEach(question => {
                    this.getQuestionsAnswers(question, selectedChoices);
                })
            })
        });

        attachment = this.attachment;
        if(!attachment.externalContent) return;
        attachment.externalContent.content = JSON.stringify(assessment);

        try {
            this.state.beginSave();

            const response = await this.mcgService.saveAssessmentAnswers(attachment);
            const responseAssessment: Mcg.Assessment = response.result.assessment;

            this.identifyProblemsAndBarriers(assessment, responseAssessment);


            const externalContentId: string | null | undefined = attachment.externalContent?.id;
            if(externalContentId) {
                const externalContent: Core.AssessmentAttachmentExternalContent = {
                    content: JSON.stringify(assessment)
                };

                await this.service.updateAttachmentExternalContent(externalContentId, externalContent);
            }

            if(selectedGuidelineLinks.length > 0) await this.saveGuidelines(selectedGuidelineLinks);

            this.coreSections?.forEach(section => {
                section.questions?.forEach(question => {
                    question.choices?.forEach(choice => {
                        const mcgChoice: Mcg.Choice | undefined = selectedChoices.find(c => c.uid == choice.externalChoiceIdentifier);
                        if(!mcgChoice) return;

                        let answer: Core.AssessmentAnswer = {
                            choice: {
                                id: choice.id,
                                externalChoiceIdentifier: mcgChoice.uid
                            },
                            question: { id: question.id }
                        };

                        if(mcgChoice.value?.length > 0) answer.text = mcgChoice.value;

                        answers.push(answer);
                    })
                })
            });

            this.context.currentSession.answers = answers;

            if(await this.saveSession()) {

                this.toaster.standard.saved('am:assessment.$');

                if(complete) {
                    if(await this.completeAttachment()) {
                        if(this.attachment) this.attachment.isCompleted = true;
                    }
                }
            }

            await this.reload();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.context.unsavedChanges = false;
            this.state.endSave();
        }
    }

    private getQuestionsAnswers(question: Mcg.Question, selectedChoices: Mcg.Choice[]): void {
        question.answersSet.forEach(choice => {
            const selectedChoice = selectedChoices.find((c) => c.uid === choice.uid);
            choice.selected = !!selectedChoice;

            if(choice.selected) {
                switch(question.inputType) {
                    case 'SingleSelect':
                    case 'MultipleSelect': {
                        if(!choice.inputType || !selectedChoice?.value) { return; }

                        switch(choice.inputType) {
                            case 'text': {
                                choice.value = selectedChoice.value;
                                break;
                            }
                        }

                        break;
                    }
                    case 'FreeText':
                    case 'DEDate':
                    case 'DENumeric': {
                        if(!selectedChoice?.value) { return; }
                        choice.value = selectedChoice.value;
                        break;
                    }
                }
            }
        });
    }

    private identifyProblemsAndBarriers(assessment: Mcg.Assessment, responseAssessment: Mcg.Assessment): void {
        if(!responseAssessment.sectionsSet) return;

        for(const section of responseAssessment.sectionsSet) {
            for(const subSection of section.sectionsSet) {
                for(const problem of subSection.problemsSet) {
                    if(!problem.identified) continue;
                    this.identifyProblem(problem.uid, assessment);
                }

                for(const barrier of subSection.barriersSet) {
                    if(!barrier.identified) continue;
                    this.identifyBarrier(barrier.uid, assessment);
                }
            }
        }
    }

    private identifyProblem(uid: string, assessment: Mcg.Assessment): void {
        for(const section of assessment.sectionsSet) {
            for(const subSection of section.sectionsSet) {
                for(const problem of subSection.problemsSet) {
                    if(problem.uid !== uid) continue;
                    problem.identified = true;
                    return;
                }
            }
        }
    }

    private identifyBarrier(uid: string, assessment: Mcg.Assessment): void {
        for(const section of assessment.sectionsSet) {
            for(const subSection of section.sectionsSet) {
                for(const barrier of subSection.barriersSet) {
                    if(barrier.uid !== uid) continue;
                    barrier.identified = true;
                    return;
                }
            }
        }
    }

    private async saveGuidelines(selectedGuidelineLinks: Mcg.GuidelineLink[]): Promise<void> {
        const caseId = this.attachment?.attachedTo?.id;
        if(!caseId) { return; }

        const promises = new Array<Promise<IApiActionResults>>();

        const mcgAssessmentList = await this.loadAssessmentsList();
        const attachments: CM.CaseAssessmentAttachment[] = [];

        selectedGuidelineLinks.forEach(g => {
            const assessment = mcgAssessmentList.find(a => a.externalAssessmentIdentifier === g.hsim);

            if(!assessment) {
                this.toaster.error().message(`${g.title} Assessment Not Found`);
                return;
            }

            if(attachments.find(a => a.assessment?.id === assessment.id)) return;

            const attachment: CM.CaseAssessmentAttachment = {
                assessment: {
                    id: assessment.id,
                    version: { id: assessment.currentVersion!.id }
                },
                case: { id: caseId }
            };

            attachments.push(attachment);
        });

        for(const attachment of attachments) {
            promises.push(this.service.insertAttachment(attachment));
        }

        try {
            this.state.beginSave();

            if(promises.length > 0) {
                const responses = await Promise.all(promises);
                const success = responses.every(r => { return r.isSuccessful; });

                if(success) this.toaster.standard.added('ext:cci.assessment.$', true, promises.length);
            }
        }
        catch(error) {
            this.toaster.standard.added('ext:cci.assessment.$', false, promises.length);
            this.logger.error('Error adding selected guideline links');
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    protected async navigateBack(): Promise<void> {
        if(!this.readonly && this.context.unsavedChanges) {
            const result = await confirmation.showCancelChanges('am:assessment.$');
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }
        }
        this.events.publish(new EnableReturnFocusEvent());
        this.router.navigateBack();
    }

    protected showQuestion(question: Mcg.Question): boolean {
        return question.topLevel || this.context.selectedQuestionReferenceIds.includes(question.id);
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadAttachment(attachmentId: string): Promise<void> {
        const includes: string[] = [
            'assessment.currentVersion',
            'assessment.externalSystem',
            'currentAnswers',
            'externalContent',
            'metadata.created.byUser'
        ];

        try {
            this.state.beginLoad();
            const response = await this.mcgService.loadAttachment(attachmentId, includes);
            if(!response.isSuccessful || response.results.length === 0) return;
            this.attachment = response.results[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadCaseAttachments(): Promise<void> {
        if(!this.attachment || !this.attachment.case || !this.attachment?.case.id) return;

        const options: Partial<IQueryLoadOptions> = { includeInactive: false };
        const includes: string[] = ['assessment'];

        try {
            this.state.beginLoad();

            const response = await this.service.loadAttachmentsByTarget(this.attachment.case.id, options, includes, false);
            if(!response.isSuccessful || response.results.length === 0) return;

            const attachments: CM.CaseAssessmentAttachment[] = response.results;
            attachments.forEach(a => {
                if(!a.assessment || !a.assessment.externalAssessmentIdentifier) return;
                this.existingAttachments.push(a.assessment.externalAssessmentIdentifier)
            });
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadAssessmentStructure(): Promise<void> {
        const versionId = safe(() => this.attachment!.assessment!.version!.id);
        if(!versionId) { throw new Error('Unable to determine assessment version id'); }

        // Ensure question resolver is initialized before loading structure
        if(!this.coreResolver.isInitialized) await this.coreResolver.init();

        try {
            this.state.beginLoad();
            const response = await this.service.loadVersionSectionsWithStructure(versionId);
            if(!response.isSuccessful) return;
            this.coreSections = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadSession(sessionId: string): Promise<void> {
        const includes: string[] = ['metadata.modified.byUser'];

        try {
            this.state.beginLoad();
            const response = await this.service.loadSession(sessionId, includes);
            if(!response.isSuccessful || response.results.length === 0) return;
            this.session = response.results[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveSession(): Promise<boolean> {
        if(!this.context.currentSession) { return false; }

        try {
            this.state.beginSave();
            const response = await this.service.saveSession(this.context.currentSession);

            if(response.isSuccessful) {
                this.context.currentSession.id = response.id;
                if(this.context.currentSession.id) this.loadSession(this.context.currentSession.id);
            }
            else {
                this.toaster.standard.saved('am:assessment.$', false);
                throw new Error(response.resultText || 'Unable to save assessment');
            }

            return response.isSuccessful;
        }
        catch(error) {
            this.toaster.standard.saved('am:assessment.$', false);
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async completeAttachment(): Promise<boolean> {
        if(!this.attachmentId) { return false; }

        try {
            this.state.beginSave();
            const response = await this.service.completeAttachment(this.attachmentId);
            this.toaster.standard.completed('am:assessment.$', response.isSuccessful);

            if(response.isSuccessful) {
                fireEvents(this.$element, 'assessment-complete');
            }
            return response.isSuccessful;
        }
        catch(error) {
            this.toaster.standard.completed('am:assessment.$', false);
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    public async loadAssessmentsList(): Promise<AssessmentWithLocalState[]> {
        const options: Partial<IQueryLoadOptions> = { all: true };
        const includes: string[] = ['externalSystem'];

        try {
            this.state.beginLoad();

            const response = await this.service.loadAssessments(options, includes, 'CCI');
            if(!response.isSuccessful || response.results.length === 0) return [];

            return response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadSettings(): Promise<IApiSettings> {
        try {
            this.state.beginLoad();

            const response = await this.caseService.loadCaseSettings();
            return response.settings;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadMcgAttachments(attachmentIds: string[]): Promise<Core.AssessmentAttachment[]> {
        const promises = new Array<Promise<IApiQueryResults<Core.AssessmentAttachment>>>();
        const includes = ['assessment'];

        try {
            attachmentIds.forEach(id => {
                promises.push(this.service.loadAttachment(id, includes));
            });

            this.state.beginLoad();
            // Execute all promises and wait until they all finish
            const responses = await Promise.all(promises);
            const attachments: Core.AssessmentAttachment[] = [];
            responses.forEach(r => {
                r.results.forEach(a => { attachments.push(a); });
            });
            return attachments;
        }
        catch(error) {
            this.logger.error('Error loading MCG attachments');
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadMcgAssessments(attachments: Core.AssessmentAttachment[]): Promise<Mcg.Assessment[]> {
        const promises = new Array<Promise<IApiActionResults>>();

        try {
            attachments.forEach(a => { if(a.id) promises.push(this.mcgService.getAssessmentContent(a.id)); });

            this.state.beginLoad();
            // Execute all promises and wait until they all finish
            const responses = await Promise.all(promises);
            const assessments: Mcg.Assessment[] = [];
            responses.forEach(r => {
                const assessment: Mcg.Assessment = r.result;
                assessments.push(assessment);
            });
            return assessments;
        }
        catch(error) {
            this.logger.error('Error adding assessments');
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async addMcgAttachmentExternalContent(mcgAttachments: Core.AssessmentAttachment[], mcgAssessments: Mcg.Assessment[]): Promise<boolean> {
        const promises = new Array<Promise<IApiActionResults>>();

        try {
            mcgAttachments.forEach(a => {
                if(!a.id) return;
                const assessment: Mcg.Assessment | undefined = mcgAssessments.find(mcg => mcg.hsim === a.assessment?.externalAssessmentIdentifier);
                if(!assessment) return;

                promises.push(this.service.insertAttachmentExternalContent({
                    assessmentAttachment: { id: a.id },
                    externalContentType: { code: 'AssessmentJson' },
                    content: JSON.stringify(assessment),
                    mediaType: { code: 'data.json' }
                } as Core.AssessmentAttachmentExternalContent));
            });

            this.state.beginSave();
            // Execute all promises and wait until they all finish
            const responses = await Promise.all(promises);

            const success = responses.every(r => { return r.isSuccessful; });
            return success;
        }
        catch(error) {
            this.logger.error('Error adding assessments');
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

}
